import { ICommand } from '@/modules/canvas/commands/ICommand';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IHistoryService } from '@/modules/canvas/services/history/IHistoryService';
import { useHistoryStore } from '@/modules/history/useHistoryStore';
import { getUserPreference } from '@/modules/userPreferences/store/getUserPreferenceSelector';
import { UserPreferenceName } from '@/modules/userPreferences';
import { logger } from '@/modules/common/logging/logger';

export class HistoryServiceImpl implements IHistoryService {
  private historyStore: typeof useHistoryStore;
  private trackingEnabled = false;
  private currentBatch: ICommand[] | null = null;
  private runningSteps: Promise<void> = Promise.resolve();

  public constructor(historyStore: typeof useHistoryStore) {
    this.historyStore = historyStore;
    this.trackingEnabled = true;
  }

  private get depth(): number {
    return getUserPreference(UserPreferenceName.APP_HISTORY_DEPTH);
  }

  startTracking(): void {
    this.trackingEnabled = true;
  }

  stopTracking(): void {
    this.trackingEnabled = false;
  }

  clearHistory(): void {
    this.historyStore.getState().reset();
  }

  track(command: ICommand): void {
    if (!this.trackingEnabled) return;

    if (this.currentBatch) {
      this.currentBatch.push(command);
      return;
    }

    this.pushToHistory(command);
  }

  batch(fn: () => void): void {
    const parentBatch = this.currentBatch;
    const collected: ICommand[] = [];
    this.currentBatch = collected;

    try {
      fn();
    } finally {
      // Always restore the parent batch, even if fn throws.
      this.currentBatch = parentBatch;
    }

    this.commitBatch(collected, parentBatch);
  }

  async batchAsync<Result>(fn: () => Promise<Result>): Promise<Result> {
    const parentBatch = this.currentBatch;
    const collected: ICommand[] = [];
    this.currentBatch = collected;
    let result: Result;

    try {
      result = await fn();
    } finally {
      // Keep collecting until the promise settles, then restore the parent batch.
      this.currentBatch = parentBatch;
    }

    this.commitBatch(collected, parentBatch);

    return result;
  }

  undo(): void {
    const lastCommand = this.historyStore.getState().past.pop();
    if (lastCommand !== undefined) {
      this.historyStore.getState().future.push(lastCommand);
      this.enqueue(() => lastCommand.undo());
    }

    this.updateHistoryState();
  }

  redo(): void {
    const lastCommand = this.historyStore.getState().future.pop();
    if (lastCommand !== undefined) {
      this.historyStore.getState().past.push(lastCommand);
      this.enqueue(() => lastCommand.execute());
    }

    this.updateHistoryState();
  }

  //
  // Private methods
  //

  /**
   * @description Runs undo/redo steps one after the other: a step can be async (e.g. regenerating
   * templates), the next must not start on a half-applied state. Autosaves once a step is applied.
   */
  private enqueue(step: () => void | Promise<void>): void {
    this.runningSteps = this.runningSteps
      .then(step)
      .catch((error: unknown) => logger.error('Failed to undo or redo.', error))
      .then(() => FddmServiceFactory.getInstance().createFddmService().triggerAutosave());
  }

  private commitBatch(collected: ICommand[], parentBatch: ICommand[] | null): void {
    if (collected.length === 0) return;

    if (parentBatch) {
      parentBatch.push(...collected);
      return;
    }

    // One after the other, awaiting async commands, like they ran the first time
    const composite: ICommand = {
      execute: async () => {
        for (const command of collected) await command.execute();
      },
      undo: async () => {
        for (const command of [...collected].reverse()) await command.undo();
      },
    };

    this.pushToHistory(composite);
  }

  private pushToHistory(command: ICommand): void {
    // Reached max undo, remove oldest entries so we stay within the configured depth.
    while (this.historyStore.getState().past.length >= this.depth) {
      this.historyStore.getState().past.shift();
    }

    this.historyStore.getState().past.push(command);

    // reset future
    this.historyStore.getState().future = [];

    this.updateHistoryState();
  }

  private hasUndo(): boolean {
    return this.historyStore.getState().past.length > 0;
  }

  private hasRedo(): boolean {
    return this.historyStore.getState().future.length > 0;
  }

  private updateHistoryState(): void {
    this.historyStore.getState().setHasUndo(this.hasUndo());
    this.historyStore.getState().setHasRedo(this.hasRedo());
  }
}
